import type { Observable } from 'rxjs';
import type { Uuid } from '@common/types';
import type {
    DenormalizedColumnEntity,
    DenormalizedSectionEntity,
    NormalizedDefaultSectionEntity,
    NormalizedSectionEntity,
    NormalizedSpecificSectionEntity,
    NormalizedZeroSectionEntity,
} from '@web/types';

import { Injectable, inject } from '@angular/core';
import { Store } from '@ngrx/store';

import {
    sectionInstance,
    specificSectionInstance,
    zeroSectionInstance,
} from '@web/store/page-structure/sections/sections.models';
import { ColumnsFacade } from '@web/store/page-structure/columns/columns.facade';

import SectionsActions from './sections.actions';
import * as SectionsSelectors from './sections.selectors';

@Injectable()
export class SectionsFacade {
    private readonly store = inject(Store);
    private columnsFacade = inject(ColumnsFacade);

    /**
     * Combine pieces of state using createSelector,
     * and expose them as observables through the facade.
     */
    public readonly sections$ = this.store.select(SectionsSelectors.selectAllSections);
    public readonly sectionsEntities$ = this.store.select(SectionsSelectors.selectSectionsEntities);

    /**
     * Use the initialization action to perform one
     * or more tasks in your Effects.
     */
    public init(sections: NormalizedSectionEntity[]): void {
        this.store.dispatch(SectionsActions.init({ sections }));
    }

    /** Narrowed to the subtype each factory actually seeds, so callers can reach its props. */
    public getSectionInstanceValue(blockId: Uuid): NormalizedDefaultSectionEntity {
        return sectionInstance({ parentId: blockId });
    }

    public getSpecificSectionInstanceValue(blockId: Uuid): NormalizedSpecificSectionEntity {
        return specificSectionInstance({ parentId: blockId });
    }

    public getZeroSectionInstanceValue(blockId: Uuid): NormalizedZeroSectionEntity {
        return zeroSectionInstance({ parentId: blockId });
    }

    public addSection(section: NormalizedSectionEntity, index: number): void {
        this.store.dispatch(SectionsActions.addSection({ section, index }));
    }

    public createSection(blockId: Uuid, index: number): void {
        this.store.dispatch(SectionsActions.createSection({ blockId, index }));
    }

    public createSpecificSection(blockId: Uuid, index: number): void {
        this.store.dispatch(SectionsActions.createSpecificSection({ blockId, index }));
    }

    public createZeroSection(blockId: Uuid, index: number): void {
        this.store.dispatch(SectionsActions.createZeroSection({ blockId, index }));
    }

    public updateSectionProperties(sectionId: Uuid, properties: Partial<NormalizedSectionEntity>): void {
        this.store.dispatch(
            SectionsActions.updateSectionProperties({ update: { id: sectionId, changes: properties } })
        );
    }

    public removeSection(section: DenormalizedSectionEntity, index: number, skipHistory: boolean = false): void {
        this.store.dispatch(SectionsActions.removeSection({ section, index, skipHistory }));
        this.columnsFacade.removeManyColumns(section.children);
    }

    public removeManySections(sections: DenormalizedSectionEntity[]): void {
        this.store.dispatch(
            SectionsActions.removeManySections({
                sectionsIds: sections.map((section: DenormalizedSectionEntity) => section.id),
            })
        );

        const columns: DenormalizedColumnEntity[] = sections.reduce(
            (result: DenormalizedColumnEntity[], section: DenormalizedSectionEntity) => {
                result.push(...section.children);
                return result;
            },
            []
        );

        this.columnsFacade.removeManyColumns(columns);
    }

    public moveUp(index: number, blockId: Uuid, skipHistory: boolean = false): void {
        this.store.dispatch(SectionsActions.moveUp({ index, blockId, skipHistory }));
    }

    public moveDown(index: number, blockId: Uuid, skipHistory: boolean = false): void {
        this.store.dispatch(SectionsActions.moveDown({ index, blockId, skipHistory }));
    }

    public sectionById$(sectionId: Uuid): Observable<NormalizedSectionEntity> {
        return this.store.select(SectionsSelectors.selectSectionById(sectionId));
    }

    public get sectionsIds$(): Observable<string[]> {
        return this.store.select(SectionsSelectors.selectSectionsIds) as Observable<string[]>;
    }

    public denormalizedSectionById$(sectionId: Uuid): Observable<DenormalizedSectionEntity> {
        return this.store.select(SectionsSelectors.selectDenormalizedSectionById(sectionId));
    }
}
